// supabaseAuth.ts
import { proxy } from 'valtio';
import { User, AuthError } from '@supabase/supabase-js';
import sb from '@/utils/sb';
import { handleError } from '@/utils/helpers';

// 定义 Supabase 认证状态接口
export interface SupabaseAuthState {
  user: User | null;
  id: string | null; // 用户ID，优先从user_metadata.pid取，否则取普通id
  isAuthenticated: boolean;
  isLoading: boolean;
  error: AuthError | Error | null;
}

// 定义用户数据类型
type UserData = {
  user: User | null;
  error: AuthError | null;
};

// 创建 Supabase 认证状态管理 store
export const supabaseAuthStore = proxy<SupabaseAuthState>({
  user: null,
  id: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
});


// Supabase 认证状态管理方法
export const supabaseAuthActions = {
  /**
   * 私有方法：更新认证状态
   * @param userData - Supabase 用户数据
   * @param error - 错误信息
   * @param shouldSetLoading - 是否设置 loading 状态
   */
  _updateAuthState(userData: { user: User | null; error: any } | null, shouldSetLoading = false): void {
    if (shouldSetLoading) {
      supabaseAuthStore.isLoading = false;
    }
    
    if (userData?.error) {
      supabaseAuthStore.user = null;
      supabaseAuthStore.id = null;
      supabaseAuthStore.isAuthenticated = false;
      supabaseAuthStore.error = userData.error;
      console.warn('认证操作失败:', userData.error.message);
    } else {
      const user = userData?.user || null;
      supabaseAuthStore.user = user;
      // 计算用户ID：优先从user_metadata.pid取，否则取普通id
      let userId = user?.user_metadata?.pid || user?.id;
      // userId = userId.replace("auth0|", "")
      userId = userId.replace("-", "")
      console.log('用户ID:', userId);
      supabaseAuthStore.id = userId;
      supabaseAuthStore.isAuthenticated = !!user;
      supabaseAuthStore.error = null;
      if (user) {
        console.log('认证状态已更新:', { 
          user: user.email, 
          id: supabaseAuthStore.id,
          isAuthenticated: true 
        });
      }
    }
  },

  /**
   * 私有方法：统一的错误处理
   * @param error - 错误对象
   * @param message - 错误消息
   */
  _handleError(error: any, message: string): void {
    console.error(`${message}:`, error);
    supabaseAuthStore.user = null;
    supabaseAuthStore.isAuthenticated = false;
    supabaseAuthStore.error = error as Error;
    handleError(error, message, 'supabaseAuth');
  },

  /**
   * 私有方法：获取用户信息的核心逻辑
   * @param setLoading - 是否设置 loading 状态
   */
  async _fetchUserData(setLoading = false): Promise<void> {
    try {
      if (setLoading) {
        supabaseAuthStore.isLoading = true;
        supabaseAuthStore.error = null;
      }

      const { data, error } = await sb.auth.getUser();
      this._updateAuthState({ user: data.user, error }, setLoading);
    } catch (error) {
      this._handleError(error, setLoading ? '初始化认证状态失败' : '更新认证状态失败');
      if (setLoading) {
        supabaseAuthStore.isLoading = false;
      }
    }
  },

  /**
   * 初始化认证状态 - 在应用启动时调用
   */
  async init(): Promise<void> {
    await this._fetchUserData(true);
  },

  /**
   * 更新认证状态 - 在登录、注册、确认等操作后调用
   */
  async update(): Promise<void> {
    await this._fetchUserData(false);
  },

  /**
   * 清除认证状态 - 在登出时调用
   */
  clear(): void {
    supabaseAuthStore.user = null;
    supabaseAuthStore.id = null;
    supabaseAuthStore.isAuthenticated = false;
    supabaseAuthStore.error = null;
    supabaseAuthStore.isLoading = false;
  },

  /**
   * 登出用户
   */
  async logout(): Promise<void> {
    try {
      const { error } = await sb.auth.signOut();
      if (error) {
        console.error('登出失败:', error.message);
        handleError(error, '登出失败', 'supabaseAuth');
      } else {
        this.clear();
        console.log('用户已登出');
      }
    } catch (error) {
      this._handleError(error, '登出过程中发生错误');
    }
  },

  


  /**
   * 检查是否正在加载
   */
  isLoading(): boolean {
    return supabaseAuthStore.isLoading;
  }
};

// 设置认证状态监听器
sb.auth.onAuthStateChange((event, session) => {
  console.log('认证状态变化:', event, session?.user?.email);
  
  switch (event) {
    case 'SIGNED_IN':
      supabaseAuthActions._updateAuthState({ user: session?.user || null, error: null }, true);
      break;
    case 'SIGNED_OUT':
      supabaseAuthActions.clear();
      break;
    case 'TOKEN_REFRESHED':
      supabaseAuthActions._updateAuthState({ user: session?.user || null, error: null });
      break;
    default:
      break;
  }
});

// 导出 hook 风格的使用方式，兼容 Auth0 接口
export const useSupabaseAuth = () => {
  return {
    user: supabaseAuthStore.user,
    id: supabaseAuthStore.id,
    isAuthenticated: supabaseAuthStore.isAuthenticated,
    isLoading: supabaseAuthStore.isLoading,
    error: supabaseAuthStore.error,
    logout: supabaseAuthActions.logout,
    // 为了兼容一些 Auth0 的使用方式，提供这些方法的别名
    loginWithRedirect: () => {
      window.location.href = '/s_auth/login';
    }
  };
};